import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCaptureFields } from '@/components/vsleads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SliderNumericField } from '@/components/ui/numeric-input';

interface LayoutTabProps {
  form: UseFormReturn<FormCaptureFields>;
}

export function LayoutTab({ form }: LayoutTabProps) {
  useLanguage();
  const layout = form.watch('layout');

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Orientação do Formulário")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Escolha como os campos serão organizados")}</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={layout.orientation}
            onValueChange={(value: 'vertical' | 'horizontal') =>
              form.setValue('layout.orientation', value, { shouldDirty: true })
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="vertical" id="vertical" />
              <Label htmlFor="vertical">{translateInterfaceText("Vertical (campos empilhados)")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="horizontal" id="horizontal" />
              <Label htmlFor="horizontal">{translateInterfaceText("Horizontal (campos lado a lado)")}</Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Posição no Vídeo")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Onde o formulário será exibido sobre o vídeo")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={layout.position}
            onValueChange={(value: 'center' | 'side') =>
              form.setValue('layout.position', value, { shouldDirty: true })
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="center" id="center" />
              <Label htmlFor="center">{translateInterfaceText("Centralizado")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="side" id="side" />
              <Label htmlFor="side">{translateInterfaceText("Lateral")}</Label>
            </div>
          </RadioGroup>

          {layout.position === 'side' && (
            <RadioGroup
              value={layout.side || 'right'}
              onValueChange={(value: 'right' | 'left') =>
                form.setValue('layout.side', value, { shouldDirty: true })
              }
              className="ml-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="right" id="right" />
                <Label htmlFor="right">{translateInterfaceText("Direita")}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="left" id="left" />
                <Label htmlFor="left">{translateInterfaceText("Esquerda")}</Label>
              </div>
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Dimensões")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Ajuste o tamanho e espaçamento do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SliderNumericField
            label={translateInterfaceText("Largura")}
            value={layout.width}
            onChange={(value) => form.setValue('layout.width', value, { shouldDirty: true })}
            min={280}
            max={640}
            step={10}
            suffix="px"
          />

          <SliderNumericField
            label={translateInterfaceText("Espaçamento Interno")}
            value={layout.padding}
            onChange={(value) => form.setValue('layout.padding', value, { shouldDirty: true })}
            min={8}
            max={48}
            step={4}
            suffix="px"
          />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Espaçamento Externo (Offset)")}</CardTitle>
          <CardDescription>
            {translateInterfaceText("Distância do formulário em relação às bordas do vídeo")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SliderNumericField
            label={translateInterfaceText("Topo")}
            value={layout.offset.top}
            onChange={(value) => form.setValue('layout.offset.top', value, { shouldDirty: true })}
            min={0}
            max={100}
            step={5}
            suffix="px"
          />
          <SliderNumericField
            label={translateInterfaceText("Direita")}
            value={layout.offset.right}
            onChange={(value) => form.setValue('layout.offset.right', value, { shouldDirty: true })}
            min={0}
            max={100}
            step={5}
            suffix="px"
          />
          <SliderNumericField
            label={translateInterfaceText("Baixo")}
            value={layout.offset.bottom}
            onChange={(value) => form.setValue('layout.offset.bottom', value, { shouldDirty: true })}
            min={0}
            max={100}
            step={5}
            suffix="px"
          />
          <SliderNumericField
            label={translateInterfaceText("Esquerda")}
            value={layout.offset.left}
            onChange={(value) => form.setValue('layout.offset.left', value, { shouldDirty: true })}
            min={0}
            max={100}
            step={5}
            suffix="px"
          />
        </CardContent>
      </Card>
    </div>
  );
}
